import { customElement } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store from "../../../store/store";
import {
  calculateTax, getBillingAddress, getBraintreeClientTokenApiStatus, getBraintreeHostedFieldsStatus, getClientTokenResponse, getClientTokenResponseError, getPaymentDetails, getTaxCalculationError, getTaxCalculationStatus, getTaxInfo, getTotalPrice,
} from '../shop-checkout';
import { BraintreeHostedFieldsStatus, ClientTokenDetails } from "../shop-checkout.interface";
import "../../payment-methods/credit-card/credit-card-payment-method";
import "../../payment-methods/paypal/paypal-payment-method";
import "../../payment-methods/po-order/po-order-payment-method";
import { PaymentBillingCheckoutStepBase } from "../payment-billing-checkout-step-base";
import { StoreState } from '../../../store/store.interface';
import { getPromoCode } from '../../../features/promo-code/promo-code.selectors';
import { ActionStatus } from '../../../common/constants/action-status';
import {
  isValidApplePayDetail,
  isValidGooglePayDetail,
  isValidPaypalDetail,
  isValidVenmoDetail
} from '../../../common/utils/payment-util';
import { MessageTemplateId } from '../../../common/constants/api-constants';
import { handleApiError } from '../../../common/utils/api-utils';
import { PaymentMethod, PaymentMethodType } from '../../payment-methods/payment-method';
import { getCart } from '../../cart/shop-cart/store/shop-cart.selectors';
import emit from '../../../common/events';
import { addAnalyticsCheckoutFlowMetadata } from '../../../common/utils/cart-util';
import { getSharpenTunnelFlowProperties } from '../../../common/utils/analytics-util';

/**
 * Checkout step to load payment options and capture payment details.
 */
@customElement('mhecomm-payment-billing-tunnel-checkout-step')
export class PaymentBillingTunnelCheckoutStep extends connect(store)(PaymentBillingCheckoutStepBase) {

  stateChanged(state: StoreState) {
    this.parentStateChanged(state);
    this.canEditStep(state);

    this.handleApiFailures(state);
    this.isLoading = getBraintreeClientTokenApiStatus(state) === ActionStatus.loading
      || (this.isActive && this.availablePaymentOptions.length < 1);
    this.handleEditBillingAddress(state);
    this.showNextStepApplePayPayment = !!isValidApplePayDetail(getPaymentDetails(state));
    this.showNextStepGooglePayPayment = !!isValidGooglePayDetail(getPaymentDetails(state));
    this.showNextStepPaypalPayment = !!isValidPaypalDetail(getPaymentDetails(state));
    this.showNextStepVenmoPayment = !!isValidVenmoDetail(getPaymentDetails(state));

    if (getBraintreeHostedFieldsStatus(state) && getBraintreeHostedFieldsStatus(state) === BraintreeHostedFieldsStatus.INVALID) {
      this.braintreeHostedFieldsErrorMessage = MessageTemplateId.BRAIN_TREE_HOSTED_FIELDS_ERROR;
    } else {
      this.braintreeHostedFieldsErrorMessage = undefined;
    }
    this.updateNextStepButton(state);
  }

  override handleApiFailures(state: StoreState){
    if (getTaxCalculationStatus(state) === ActionStatus.failed) {
      handleApiError(getTaxCalculationError(state));
      return;
    }
    if(getBraintreeClientTokenApiStatus(state) === ActionStatus.failed){
      handleApiError(getClientTokenResponseError(state));
      return;
    }
  }

  /**
   * Check if Paypal payment should be enabled.
   */
  protected override isPaypalEnabled(payload: ClientTokenDetails): boolean {
    return payload.paypalEnabled;
  }

  /**
   * On click Review Order button, validate payment method and then calculate tax.
   */
   override async stepDoValidation() {
    if(this.selectedPaymentMethodType?.component) {
      this.selectedPaymentMethodEl = this.shadowRoot!.querySelector(this.selectedPaymentMethodType?.component) as PaymentMethod;
      try {
        await this.selectedPaymentMethodEl.validatePaymentMethod()
        await store.dispatch(calculateTax()).unwrap();
        this.reviewOrderAnalytics();
      }
      catch(error) {
        console.error(`Failed to validate ${this.selectedPaymentMethodType.displayName}`,error);
        throw error;
      }
    }
  }

  reviewOrderAnalytics() {
    const paymentDetails = getPaymentDetails(store.getState() as StoreState)
    const product = getCart(store.getState() as StoreState)!.products[0]
    const clientTokenResponse = getClientTokenResponse(store.getState() as StoreState)!
    
    emit({
      name: "review-order-tunnel-checkout",
      element: this,
      detail: addAnalyticsCheckoutFlowMetadata({
        price: `${product.priceInfo.customPrice} ${product.priceInfo.currencyCode}`,
        countryCode: product.priceInfo.countryCode,
        promoCode: getPromoCode(store.getState() as StoreState),
        estimatedTotal: getTotalPrice(store.getState() as StoreState),
        paymentType: PaymentMethodType.getPaymentMethodType(paymentDetails!.paymentType!).displayName,
        threeDS: clientTokenResponse?.threeDSecureEnabled,
        unit: getBillingAddress(store.getState() as StoreState)?.address2,
        taxDetails: getTaxInfo(store.getState() as StoreState),
        ...getSharpenTunnelFlowProperties({
          isSharpenCompanion: product.productIdentifier.isSharpenCompanion,
          isSharpenAdvantage: product.productIdentifier.isSharpenAdvantage,
          isConnectSharpenBundle: product.productIdentifier.isConnectSharpenBundle,
          itemType: product.productMetadata.itemType,
          offerType: product.productMetadata.offerType,
          formatCode: product.productMetadata.formatCode,
          flow: product.productMetadata.flow
        })
      })
    })
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-payment-billing-tunnel-checkout-step': PaymentBillingTunnelCheckoutStep;
  }
}
